import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { CheckmarkCircleRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { HoursApi } from "@/services/api/hours-api";
import { HoursTable, type HoursTableRow } from "./HoursTable";
import type {
  EnergyHoursProject,
  EnergyHoursResponse,
  EnergyHoursSaveRow,
  PowerHoursProject,
  PowerHoursResponse,
  TechnologyHoursProject,
  TechnologyHoursResponse,
  WeeklyHoursFormKind,
} from "./weekly-hours-types";

type DedicatedKind = Extract<WeeklyHoursFormKind, "energy" | "energy-si" | "technology" | "power">;

interface DraftRow {
  projectId: number;
  taskId: number;
  projectName: string;
  context: string;
  first: string;
  second: string;
  third: string;
  originalFirst: number | null;
  originalSecond: number | null;
  originalThird: number | null;
  actualTotal: number | null;
  editable: [boolean, boolean, boolean];
  lockedReason: string | null;
}

interface DedicatedConfig {
  title: string;
  subtitle: string;
  labels: [string, string, string];
  shortLabels: [string, string, string];
  estimateLabel: string;
  integerOnly: boolean;
}

const CONFIG: Record<DedicatedKind, DedicatedConfig> = {
  energy: {
    title: "Energy weekly hours",
    subtitle: "Enter blank weekly fields for each active Energy deal. Saved banker entries become read-only.",
    labels: ["Last Wk's Hrs (M-F)", "Last Wknd Hrs (S-S)", "Current Est. Hrs (Nxt 7 Days)"],
    shortLabels: ["M–F", "S–S", "Est."],
    estimateLabel: "Next 7 days",
    integerOnly: false,
  },
  "energy-si": {
    title: "Energy SI weekly hours",
    subtitle: "Enter blank weekly fields for each active Energy SI deal. Saved banker entries become read-only.",
    labels: ["Last Wk's Hrs (M-F)", "Last Wknd Hrs (S-S)", "Current Est. Hrs (Nxt 7 Days)"],
    shortLabels: ["M–F", "S–S", "Est."],
    estimateLabel: "Next 7 days",
    integerOnly: false,
  },
  technology: {
    title: "Technology weekly hours",
    subtitle: "Enter whole-hour values for your active Technology deals. Existing values can be revised.",
    labels: ["Last Week Hours", "Weekend Hours", "Current Week Hours"],
    shortLabels: ["Last wk", "Weekend", "Current"],
    estimateLabel: "Current week",
    integerOnly: true,
  },
  power: {
    title: "PU&I weekly hours",
    subtitle: "Update deal hours and, when available, your overall capacity.",
    labels: ["Hrs Spent Last Wk", "Wknd Hrs Spent", "Est Hrs for Next Wk"],
    shortLabels: ["Last wk", "Weekend", "Next wk"],
    estimateLabel: "Next week",
    integerOnly: false,
  },
};

function toInput(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}

function toNullableNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toTechnologyNumber(value: string): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
}

function formatHours(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
}

const FIELDS = ["first", "second", "third"] as const;

function sameNumber(left: number | null, right: number | null): boolean {
  return left === right;
}

function energyRows(projects: EnergyHoursProject[]): DraftRow[] {
  return projects.map((project) => {
    const overdue = !!project.IsOverdue;
    return {
      projectId: project.ProjectId,
      taskId: project.TaskId,
      projectName: project.ProjectName,
      // On the Energy time-log endpoints GroupName IS the live status — the API
      // resolves the deal's "Live Deal" toggle to exactly "Live"/"Non-Live".
      context: project.GroupName || project.Company || "Energy deal",
      first: toInput(project.LastWeekHours),
      second: toInput(project.WeekendHours),
      third: toInput(project.NextWeekHours),
      originalFirst: project.LastWeekHours,
      originalSecond: project.WeekendHours,
      originalThird: project.NextWeekHours,
      actualTotal: project.ActualTotalHours ?? null,
      editable: [
        project.LastWeekHours == null && !overdue,
        project.WeekendHours == null && !overdue,
        project.NextWeekHours == null && !overdue,
      ],
      lockedReason: overdue ? "Extend the deal due date in TRIYO to log hours." : null,
    };
  });
}

function technologyRows(projects: TechnologyHoursProject[]): DraftRow[] {
  return projects.map((project) => {
    const isOwner = !!project.IsOwner;
    return {
      projectId: project.ProjectId,
      taskId: project.TaskId,
      projectName: project.ProjectName,
      context: isOwner ? "Project owner" : "Technology deal",
      first: toInput(project.LastWeekHours),
      second: toInput(project.WeekendHours),
      third: toInput(project.CurrentWeekHours),
      originalFirst: project.LastWeekHours,
      originalSecond: project.WeekendHours,
      originalThird: project.CurrentWeekHours,
      actualTotal: null,
      editable: [!isOwner, !isOwner, !isOwner],
      lockedReason: isOwner ? "Project owners cannot enter banker hours for this deal." : null,
    };
  });
}

function powerRows(projects: PowerHoursProject[]): DraftRow[] {
  return projects.map((project) => {
    const overdue = !!project.IsOverdue || project.TaskId <= 0;
    return {
      projectId: project.ProjectId,
      taskId: project.TaskId,
      projectName: project.ProjectName,
      context: project.ClientName || project.GroupName || "PU&I deal",
      first: toInput(project.LastWeekHours),
      second: toInput(project.WeekendHours),
      third: toInput(project.NextWeekHours),
      originalFirst: project.LastWeekHours,
      originalSecond: project.WeekendHours,
      originalThird: project.NextWeekHours,
      actualTotal: null,
      editable: [!overdue, !overdue, !overdue],
      lockedReason: overdue ? "This field is currently disabled." : null,
    };
  });
}

function responseRows(
  kind: DedicatedKind,
  response: EnergyHoursResponse | TechnologyHoursResponse | PowerHoursResponse
): DraftRow[] {
  if (kind === "technology") return technologyRows((response as TechnologyHoursResponse).Projects ?? []);
  if (kind === "power") return powerRows((response as PowerHoursResponse).Projects ?? []);
  return energyRows((response as EnergyHoursResponse).Projects ?? []);
}

function rowHasError(row: DraftRow, integerOnly: boolean): boolean {
  return [row.first, row.second, row.third].some((value) => {
    if (value.trim() === "") return false;
    const parsed = Number(value);
    return !Number.isFinite(parsed) || parsed < 0 || (integerOnly && !Number.isInteger(parsed));
  });
}

function rowChanged(row: DraftRow, integerOnly: boolean): boolean {
  const parse = integerOnly ? (value: string) => toTechnologyNumber(value) : toNullableNumber;
  return (
    !sameNumber(parse(row.first), row.originalFirst) ||
    !sameNumber(parse(row.second), row.originalSecond) ||
    !sameNumber(parse(row.third), row.originalThird)
  );
}

interface DedicatedHoursFormProps {
  kind: DedicatedKind;
}

export function DedicatedHoursForm({ kind }: DedicatedHoursFormProps): JSX.Element {
  const config = CONFIG[kind];
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [capacity, setCapacity] = useState("");
  const [originalCapacity, setOriginalCapacity] = useState("");
  const [capacityTaskId, setCapacityTaskId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const applyResponse = useCallback(
    (response: EnergyHoursResponse | TechnologyHoursResponse | PowerHoursResponse) => {
      // The PU&I web client only replaces the grid when the response carries a
      // Projects array; never wipe the rows on a shape-less response.
      if (Array.isArray(response.Projects)) setRows(responseRows(kind, response));
      if (kind === "power") {
        const power = response as PowerHoursResponse;
        const nextCapacity = power.OverallCapacity ?? "";
        setCapacity(nextCapacity);
        setOriginalCapacity(nextCapacity);
        setCapacityTaskId(power.OverallCapacityTaskId ?? null);
      }
    },
    [kind]
  );

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response =
        kind === "technology"
          ? await HoursApi.getTechnologyHours()
          : kind === "power"
            ? await HoursApi.getPowerHours()
            : await HoursApi.getEnergyHours(kind === "energy-si");
      if (requestId === requestIdRef.current) applyResponse(response);
    } catch (reason: unknown) {
      if (requestId === requestIdRef.current) {
        setError(reason instanceof Error ? reason.message : "Could not load weekly hours.");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [applyResponse, kind]);

  useEffect(() => {
    void load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [load]);

  const hasErrors = rows.some((row) => rowHasError(row, config.integerOnly));
  const changedRows = useMemo(
    () => rows.filter((row) => rowChanged(row, config.integerOnly)),
    [config.integerOnly, rows]
  );
  const capacityChanged = kind === "power" && capacity !== originalCapacity;
  const canSubmit = !saving && !hasErrors && (changedRows.length > 0 || capacityChanged);

  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, row) => ({
          first: sum.first + (toNullableNumber(row.first) ?? 0),
          second: sum.second + (toNullableNumber(row.second) ?? 0),
          third: sum.third + (toNullableNumber(row.third) ?? 0),
        }),
        { first: 0, second: 0, third: 0 }
      ),
    [rows]
  );

  const updateField = (taskId: number, field: "first" | "second" | "third", value: string) => {
    setRows((current) => current.map((row) => (row.taskId === taskId ? { ...row, [field]: value } : row)));
    setSuccess(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      let response: EnergyHoursResponse | TechnologyHoursResponse | PowerHoursResponse;
      if (kind === "technology") {
        response = await HoursApi.saveTechnologyHours(
          changedRows.map((row) => ({
            ProjectId: row.projectId,
            TaskId: row.taskId,
            LastWeekHours: toTechnologyNumber(row.first),
            WeekendHours: toTechnologyNumber(row.second),
            CurrentWeekHours: toTechnologyNumber(row.third),
          }))
        );
      } else if (kind === "power") {
        // Match Triyo.Web: attach capacity to the payload only when it was actually edited.
        // Sending OverallCapacityTaskId > 0 makes the API rewrite capacity and move the
        // capacity task to InProgress, which must not happen on an hours-only save.
        response = await HoursApi.savePowerHours({
          DealsToEdit: changedRows.map((row) => ({
            ProjectId: row.projectId,
            TaskId: row.taskId,
            LastWeekHours: toNullableNumber(row.first),
            WeekendHours: toNullableNumber(row.second),
            NextWeekHours: toNullableNumber(row.third),
          })),
          OverallCapacity: capacityChanged ? capacity || null : null,
          OverallCapacityTaskId: capacityChanged && capacityTaskId != null ? capacityTaskId : 0,
        });
      } else {
        const payload: EnergyHoursSaveRow[] = changedRows.map((row) => ({
          ProjectId: row.projectId,
          TaskId: row.taskId,
          LastWeekHours: toNullableNumber(row.first),
          WeekendHours: toNullableNumber(row.second),
          NextWeekHours: toNullableNumber(row.third),
        }));
        response = await HoursApi.saveEnergyHours(kind === "energy-si", payload);
      }
      if (kind !== "power" && !response.Projects?.length) {
        // Triyo.Web treats an Energy or Technology save response without deals as a
        // failed save; keep the draft instead of wiping the grid while claiming success.
        setError("Could not save your changes.");
        return;
      }
      applyResponse(response);
      setSuccess("Weekly hours saved to TRIYO.");
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Could not save weekly hours.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="weekly-hours__state" role="status">
        <FluentSpinner label="Loading weekly hours…" />
      </div>
    );
  }

  if (error && rows.length === 0) {
    return (
      <div className="weekly-hours__state" role="alert">
        <p>{error}</p>
        <button type="button" className="weekly-hours__secondary-button" onClick={() => void load()}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="weekly-hours__form">
      <div className="weekly-hours__intro">
        <h3>{config.title}</h3>
        <p>{config.subtitle}</p>
      </div>

      {kind === "power" ? (
        <label className="weekly-hours__capacity">
          <span>Overall Capacity</span>
          <select
            value={capacity}
            disabled={capacityTaskId == null}
            onChange={(event) => {
              setCapacity(event.target.value);
              setSuccess(null);
            }}
          >
            <option value="" disabled hidden>
              Select capacity
            </option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
          {capacityTaskId == null ? <small>Ask your staffer to enable Overall Capacity tracking.</small> : null}
        </label>
      ) : null}

      {rows.length === 0 ? (
        <div className="weekly-hours__empty">You do not have any active, eligible deal tasks for this form.</div>
      ) : (
        <HoursTable
          ariaLabel={config.title}
          columns={[
            { short: config.shortLabels[0], full: config.labels[0] },
            { short: config.shortLabels[1], full: config.labels[1] },
            { short: config.shortLabels[2], full: config.labels[2] },
          ]}
          integerOnly={config.integerOnly}
          rows={rows.map((row): HoursTableRow => {
            const invalid = rowHasError(row, config.integerOnly);
            const rowTotal =
              kind === "technology" ? (
                <p className="weekly-hours__row-total">
                  Total (last wk + wknd):{" "}
                  {formatHours((toNullableNumber(row.first) ?? 0) + (toNullableNumber(row.second) ?? 0))}h
                </p>
              ) : (kind === "energy" || kind === "energy-si") && row.actualTotal != null ? (
                <p className="weekly-hours__row-total">Actual total: {formatHours(row.actualTotal)}h</p>
              ) : null;
            const lockNote = row.lockedReason ? (
              <p className="weekly-hours__lock-note">{row.lockedReason}</p>
            ) : row.editable.some((editable) => !editable) ? (
              <p className="weekly-hours__lock-note">Saved Energy values are read-only for banker self-entry.</p>
            ) : null;
            const validation = invalid ? (
              <p className="weekly-hours__validation" role="alert">
                Enter {config.integerOnly ? "whole " : ""}hours greater than or equal to zero.
              </p>
            ) : null;
            return {
              key: `${row.projectId}-${row.taskId}`,
              id: row.taskId,
              name: row.projectName,
              context: row.context,
              locked: !!row.lockedReason,
              values: [row.first, row.second, row.third],
              editable: row.editable,
              ariaLabels: [
                `${config.labels[0]} for ${row.projectName}`,
                `${config.labels[1]} for ${row.projectName}`,
                `${config.labels[2]} for ${row.projectName}`,
              ],
              notes:
                rowTotal || lockNote || validation ? (
                  <>
                    {rowTotal}
                    {lockNote}
                    {validation}
                  </>
                ) : null,
            };
          })}
          totals={[formatHours(totals.first), formatHours(totals.second), `${formatHours(totals.third)}h`]}
          onValueChange={(taskId, index, value) => updateField(taskId, FIELDS[index], value)}
          onValueBlur={
            config.integerOnly
              ? (taskId, index, value) => {
                  if (value.trim() === "") updateField(taskId, FIELDS[index], "0");
                }
              : undefined
          }
        />
      )}

      {error ? (
        <div className="weekly-hours__message weekly-hours__message--error" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="weekly-hours__message weekly-hours__message--success" role="status">
          <CheckmarkCircleRegular />
          {success}
        </div>
      ) : null}

      {rows.length > 0 || capacityTaskId != null ? (
        <div className="weekly-hours__actions">
          <button
            type="button"
            className="weekly-hours__secondary-button"
            disabled={saving}
            onClick={() => void load()}
          >
            Reset
          </button>
          <button
            type="button"
            className="weekly-hours__primary-button"
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {saving ? "Saving…" : "Submit changes"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
